import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tutorial, TutorialStep, TutorialProgress } from '../types/tutorial';

interface TutorialContextType {
  // Current tutorial state
  activeTutorial: Tutorial | null;
  currentStep: number;
  isActive: boolean;
  
  // Actions
  startTutorial: (tutorial: Tutorial, fromStep?: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  completeTutorial: () => void;
  
  // Getters
  getCurrentStep: () => TutorialStep | null;
  getTutorialProgress: (tutorialId: string) => TutorialProgress | null;
  hasCompletedTutorial: (tutorialId: string) => boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

interface TutorialProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'tutorial_progress';

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTutorial, setActiveTutorial] = useState<Tutorial | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [tutorialProgress, setTutorialProgress] = useState<TutorialProgress[]>([]);

  // Load tutorial progress from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setTutorialProgress(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to load tutorial progress:', error);
      }
    }
  }, []);

  // Save tutorial progress to localStorage
  const saveTutorialProgress = (progress: TutorialProgress[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    setTutorialProgress(progress);
  };

  const startTutorial = (tutorial: Tutorial, fromStep: number = 0) => {
    setActiveTutorial(tutorial);
    setCurrentStep(fromStep);
    setIsActive(true);

    // Navigate to the first step's route if specified
    const firstStep = tutorial.steps[fromStep];
    if (firstStep?.route && location.pathname !== firstStep.route) {
      navigate(firstStep.route);
    }

    // Call onShow for the first step
    if (firstStep?.onShow) {
      setTimeout(() => firstStep.onShow!(), 300);
    }
  };

  const nextStep = () => {
    if (!activeTutorial) return;

    const currentStepData = activeTutorial.steps[currentStep];
    
    // Call onNext callback if exists
    if (currentStepData?.onNext) {
      currentStepData.onNext();
    }

    const nextStepIndex = currentStep + 1;

    if (nextStepIndex >= activeTutorial.steps.length) {
      // Tutorial completed
      completeTutorial();
      return;
    }

    setCurrentStep(nextStepIndex);

    // Navigate to next step's route if specified
    const nextStepData = activeTutorial.steps[nextStepIndex];
    if (nextStepData?.route && location.pathname !== nextStepData.route) {
      navigate(nextStepData.route);
    }

    // Call onShow for the next step
    if (nextStepData?.onShow) {
      setTimeout(() => nextStepData.onShow!(), 300);
    }
  };

  const previousStep = () => {
    if (!activeTutorial || currentStep === 0) return;

    const prevStepIndex = currentStep - 1;
    setCurrentStep(prevStepIndex);

    // Navigate to previous step's route if specified
    const prevStepData = activeTutorial.steps[prevStepIndex];
    if (prevStepData?.route && location.pathname !== prevStepData.route) {
      navigate(prevStepData.route);
    }

    // Call onShow for the previous step
    if (prevStepData?.onShow) {
      setTimeout(() => prevStepData.onShow!(), 300);
    }
  };

  const skipTutorial = () => {
    setActiveTutorial(null);
    setCurrentStep(0);
    setIsActive(false);
  };

  const completeTutorial = () => {
    if (!activeTutorial) return;

    // Mark tutorial as completed
    const newProgress: TutorialProgress = {
      tutorialId: activeTutorial.id,
      currentStep: activeTutorial.steps.length,
      completed: true,
      completedAt: new Date().toISOString(),
    };

    const existingIndex = tutorialProgress.findIndex(
      (p) => p.tutorialId === activeTutorial.id
    );

    let updatedProgress: TutorialProgress[];
    if (existingIndex >= 0) {
      updatedProgress = [...tutorialProgress];
      updatedProgress[existingIndex] = newProgress;
    } else {
      updatedProgress = [...tutorialProgress, newProgress];
    }

    saveTutorialProgress(updatedProgress);

    // Clear active tutorial
    setActiveTutorial(null);
    setCurrentStep(0);
    setIsActive(false);
  };

  const getCurrentStep = (): TutorialStep | null => {
    if (!activeTutorial) return null;
    return activeTutorial.steps[currentStep] || null;
  };

  const getTutorialProgress = (tutorialId: string): TutorialProgress | null => {
    return tutorialProgress.find((p) => p.tutorialId === tutorialId) || null;
  };

  const hasCompletedTutorial = (tutorialId: string): boolean => {
    const progress = getTutorialProgress(tutorialId);
    return progress?.completed || false;
  };

  const value: TutorialContextType = {
    activeTutorial,
    currentStep,
    isActive,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    getCurrentStep,
    getTutorialProgress,
    hasCompletedTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};



